import { makeStyles } from "tss-react/mui";
import { toKebabCase } from "@miplatform/utils";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
} from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * @param {Object} props - Component properties
 * @param {string} [props.name=""] - Name attribute for the input field
 * @param {string} [props.className] - Additional CSS class name for the component
 * @param {string} [props.label] - Label text displayed above the select box
 * @param {string} [props.fieldType="select"] - Type of field, defaults to "select"
 * @param {SelectOption[]} [props.options=[]] - Array of option objects to display in the dropdown
 * @param {boolean} [props.required] - Whether the field is required for form validation
 * @param {boolean} [props.disabled=false] - Whether the select box is disabled
 * @param {boolean} [props.readOnly=false] - Whether the select box is read-only
 * @param {Object} [props.sx] - Material-UI sx prop for custom styling
 * @param {boolean} [props.disableNone] - Whether to hide the default "None" option
 * @param {Function} [props.handleChange] - Callback function called when selection changes
 * @param {string|number} [props.value] - Currently selected value
 * @param {Object} [props.style] - Inline styles object
 * @param {ErrorObject} [props.error] - Error object containing validation message
 * @param {Object} [props.InputProps] - Additional props passed to TextField InputProps
 * @param {Object} [props.SelectProps] - Additional props passed to TextField SelectProps
 * @param {Object} [props.menuItemStyle] - Custom styles applied to menu items
 * @param {CustomOptionProps} [props.customOptionProps] - Configuration for custom option element
 */

export function MiInputSelectBox({
  name = "",
  className,
  label,
  fieldType = "select",
  options = [],
  required,
  disabled = false,
  readOnly = false,
  sx,
  disableNone,
  handleChange,
  value,
  style,
  error,
  InputProps,
  InputLabelProps,
  SelectProps,
  menuItemStyle,
  customOptionProps,
  renderOption,
  placeholder = "--",
}) {
  const { classes } = useStyles({ style });

  const isValidValue = options.some((o) => o.value === value);
  const validValue = isValidValue ? value : "";

  let renderValue;
  if (SelectProps && SelectProps.renderValue) {
    renderValue = SelectProps.renderValue;
  } else if (readOnly && !validValue) {
    renderValue = () => placeholder;
  }

  return (
    <FormControl className={classes.selectBox} error={!!error}>
      <TextField
        className={className}
        sx={sx}
        value={validValue}
        onChange={handleChange}
        size="small"
        select={fieldType === "select"}
        label={label}
        required={required}
        error={!!error}
        SelectProps={{
          "data-testid": `mi-select-box-${toKebabCase(label || name)}`,
          MenuProps: {
            sx: {
              "& .MuiList-root.MuiMenu-list": {
                padding: 0,
              },
            },
          },
          displayEmpty: !!renderValue,
          renderValue,
          ...SelectProps,
        }}
        InputLabelProps={{
          shrink: value ?? !!renderValue,
          ...InputLabelProps,
        }}
        InputProps={{
          readOnly,
          ...InputProps,
        }}
        disabled={disabled}
        readOnly={readOnly}
        style={style}
      >
        {!disableNone && (
          <MenuItem
            value="None"
            data-testid="disable-none"
            style={menuItemStyle}
          >
            None
          </MenuItem>
        )}
        {options?.map((option, index) => (
          <MenuItem
            data-testid={`mi-selectbox-option-${toKebabCase(
              label || name
            )}-${index}`}
            key={option.value}
            value={option.value}
            style={{ ...menuItemStyle, color: option.color || "inherit" }}
            disabled={!!option.disabled}
          >
            {renderOption ? (
              renderOption(option)
            ) : (
              <>
                {option.icon && <MiIcon {...option.icon} />}
                {option.label}
              </>
            )}
          </MenuItem>
        ))}
        {customOptionProps && (
          <MenuItem
            data-testid={
              customOptionProps.testId || "mi-selectbox-option-custom"
            }
            key={customOptionProps.key}
            value={customOptionProps.value}
            style={menuItemStyle}
          >
            {customOptionProps.element}
          </MenuItem>
        )}
      </TextField>
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
}

const useStyles = makeStyles()((_, { style }) => ({
  selectBox: { width: "100%", ...style },
}));
